import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    InputSignal,
    OnInit,
    output,
    OutputEmitterRef,
    signal,
    WritableSignal
} from "@angular/core";
import {NotificationService} from "../../notification/notification.service";
import {TranslateModule} from '@ngx-translate/core';
import {FormsModule} from '@angular/forms';
import {NgOptimizedImage} from '@angular/common';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';
import {ExternalApiBridgeService, GeocodeResult, LatLngBounds, PlacePrediction} from '../../external-api/external-api-bridge.service';

export class LocationSearchCoordinates {
    constructor(public location: string,
                public lat: number,
                public lng: number,
                public stateOnly: string,
                public countryOnly: string) {
    }
}

/**
 * Autocomplete component to search for a location using the Google API
 */
@Component({
    selector: "woa-location-search-panel",
    templateUrl: "./location-search-panel.component.html",
    styleUrl: "./location-search-panel.component.scss",
    imports: [
        FormsModule,
        TranslateModule,
        NgOptimizedImage,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationSearchPanelComponent implements OnInit {
    private notificationService: NotificationService = inject(NotificationService);
    private externalApiBridgeService: ExternalApiBridgeService = inject(ExternalApiBridgeService);

    public readonly accuracy: InputSignal<number> = input.required<number>();
    public readonly latitude: InputSignal<number> = input.required<number>();
    public readonly longitude: InputSignal<number> = input.required<number>();

    public onSelect: OutputEmitterRef<LocationSearchCoordinates> = output<LocationSearchCoordinates>();

    public readonly predictions: WritableSignal<PlacePrediction[]> = signal<PlacePrediction[]>([]);
    public readonly selectionHidden: WritableSignal<boolean> = signal<boolean>(false);

    private _searchText: string = "";
    private hasLocationSelection: boolean; // flag to tell if there is a current selection
    private bounds: LatLngBounds; // Current location to bias the auto complete

    ngOnInit(): void {
        if (this.latitude() != null && this.longitude() != null && this.accuracy()) {
            const latOffset = this.accuracy() / 111320; // 1 deg latitude ~ 111.32 km
            const lngOffset = this.accuracy() / (111320 * Math.cos(this.latitude() * Math.PI / 180));
            this.bounds = {
                north: this.latitude() + latOffset,
                south: this.latitude() - latOffset,
                east: this.longitude() + lngOffset,
                west: this.longitude() - lngOffset
            };
        }
    }

    get searchText(): string {
        return this._searchText;
    }

    set searchText(searchText: string) {
        this._searchText = searchText;

        this.predictions.set([]);
        this.selectionHidden.set(false);

        if (this._searchText) {
            (async () => {
                try {
                    this.predictions.set(await this.externalApiBridgeService.getPlacePredictions(this._searchText, "AU", this.bounds));
                } catch (e) {
                    console.error("Error fetching place predictions", e);
                }
            })();
        } else if (this.hasLocationSelection) {
            this.onSelect.emit(undefined);
            this.hasLocationSelection = false;
        }
    }

    async selectLocation(prediction: PlacePrediction): Promise<void> {
        this._searchText = prediction.description;
        this.predictions.set([]);
        this.selectionHidden.set(false);

        if (prediction.primaryType === "country") {
            this.hasLocationSelection = true;
            this.onSelect.emit(new LocationSearchCoordinates(this._searchText, null, null, null, prediction.primaryTermValue));
        } else if (prediction.primaryType === "administrative_area_level_1") {
            this.hasLocationSelection = true;
            this.onSelect.emit(new LocationSearchCoordinates(this._searchText, null, null, prediction.primaryTermValue, null));
        } else {
            try {
                const geocodeResult: GeocodeResult = await this.externalApiBridgeService.geocode({placeId: prediction.placeId});
                if (!geocodeResult) {
                    return this.notificationService.displayAlert("woa.heading.address.invalidAddress", "woa.message.address.invalidAddress");
                }
                this.hasLocationSelection = true;
                this.onSelect.emit(new LocationSearchCoordinates(this._searchText, geocodeResult.location.lat, geocodeResult.location.lng, null, null));
            } catch (e) {
                console.error("Geocoding failed", e);
                return this.notificationService.displayAlert("woa.heading.address.invalidAddress", "woa.message.address.invalidAddress");
            }
        }
    }
}
